<template>
  <div
    class="wl-segmented-buttons flex w-min rounded-2xl bg-muted p-0.5"
    :class="[{ 'flex-col': isVertical }]"
  >
    <template
      v-for="(buttonGroup, buttonGroupKey) in buttons"
      :key="buttonGroupKey"
    >
      <template
        v-for="(
          {
            icon,
            isDisabled,
            ariaLabel,
            hasCurrentColor,
            onMouseEnter,
            onMouseLeave,
            onClick,
          },
          buttonKey
        ) in buttonGroup"
        :key="buttonKey"
      >
        <button
          :aria-label="ariaLabel"
          type="button"
          class="wl-segmented-buttons-button flex cursor-pointer items-center justify-center rounded-xl border-none bg-transparent text-muted outline-none transition-colors duration-100 hover:bg-emphasis focus-visible:outline-1 focus-visible:outline-offset-0 focus-visible:outline-accent active:bg-emphasis disabled:cursor-not-allowed disabled:opacity-20"
          :class="[variant === SegmentedButtonsVariant.LARGE ? 'p-2' : 'p-1.5']"
          :disabled="isDisabled"
          @mouseenter="
            {
              onMouseEnter?.();
              handleHover(buttonGroupKey, buttonKey);
            }
          "
          @mouseleave="
            {
              onMouseLeave?.();
              handleHover(undefined, undefined);
            }
          "
          @click="handleClick(onClick)"
        >
          <WLIcon
            :name="icon"
            :has-current-color="hasCurrentColor"
            :size="iconSize"
          />
        </button>
        <div
          v-if="
            buttonKey === buttonGroup.length - 1 &&
            buttonGroupKey !== buttons.length - 1
          "
          class="wl-segmented-buttons__divider bg-emphasis transition-opacity duration-100"
          :class="[
            {
              'is-invisible opacity-0': isInvisible(buttonGroupKey),
            },
            isVertical ? 'mx-2 h-px' : 'my-2 w-px',
          ]"
        />
      </template>
    </template>
  </div>
</template>

<script lang="ts" setup>
import {
  type SegmentedButtonsProps,
  SegmentedButtonsVariant,
  SegmentedButtonsOrientation,
} from "./types";
import { computed, ref, toRefs } from "vue";
import { WLIcon, IconSize } from "../icon";
import { useWebHaptics } from "../../composables";

const props = withDefaults(defineProps<SegmentedButtonsProps>(), {
  variant: SegmentedButtonsVariant.DEFAULT,
  orientation: SegmentedButtonsOrientation.HORIZONTAL,
});

const { triggerHaptic } = useWebHaptics();
const { variant, orientation, buttons } = toRefs(props);
const hoverIndex = ref<[number | undefined, number | undefined]>([
  undefined,
  undefined,
]);

const iconSize = computed(() => {
  switch (variant.value) {
    case SegmentedButtonsVariant.LARGE:
      return IconSize.MEDIUM;
    case SegmentedButtonsVariant.DEFAULT:
    default:
      return IconSize.SMALL;
  }
});

const isInvisible = (buttonGroupKey: number): boolean => {
  const buttonGroup = buttons.value[buttonGroupKey];

  const x =
    hoverIndex.value[0] === buttonGroupKey &&
    hoverIndex.value[1] === buttonGroup.length - 1 &&
    !buttonGroup[buttonGroup.length - 1]?.isDisabled;

  const y =
    hoverIndex.value[0] === buttonGroupKey + 1 &&
    hoverIndex.value[1] === 0 &&
    !buttons.value[buttonGroupKey + 1]?.[0]?.isDisabled;

  return x || y;
};

// TODO: should be slots instead of v-for
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const handleClick = (onClick: (...args: any[]) => void): void => {
  triggerHaptic("rigid");
  onClick();
};

const isVertical = computed(
  () => orientation.value === SegmentedButtonsOrientation.VERTICAL,
);

const handleHover = (
  groupIndex: number | undefined,
  buttonIndex: number | undefined,
): void => {
  hoverIndex.value = [groupIndex, buttonIndex];
};
</script>
